import { ErrorHandler } from '@angular/core';
import * as Sentry from '@sentry/angular';

const delegate = Sentry.createErrorHandler();

// MatIcon reports a failed SVG fetch as
//   `Error retrieving icon ${namespace}:${name}! ${err.message}`
// and HttpErrorResponse formats itself as
//   `Http failure response for ${url}: ${status} ${statusText}`
// so an aborted icon fetch reads:
//   "Error retrieving icon :restnot! Http failure response for assets/x.svg: 0 undefined"
//
// Status 0 means the browser cancelled the request, almost always because the
// page navigated away mid-flight, not because the icon is missing. Those are not
// actionable and drowned out real errors. Any other status (a genuine 404 or 403
// on an icon) still reports.
//
// The status is only reachable by matching the text: MatIcon wraps the
// HttpErrorResponse in a fresh Error and keeps nothing but the message. That
// couples this to Material's wording, so it is worth re-checking on major
// Material upgrades.
const ABORTED_ICON_FETCH = /Error retrieving icon .*Http failure response for .*: 0\b/;

function messageOf(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object') {
    // Unhandled rejections arrive wrapped, with the real error under `rejection`.
    const candidate = (error as { rejection?: unknown }).rejection ?? error;
    const message = (candidate as { message?: unknown }).message;
    if (typeof message === 'string') return message;
  }
  return '';
}

/**
 * Sentry's ErrorHandler with cancelled-request icon failures filtered out.
 * Everything else is passed through untouched.
 */
export class FilteredSentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (ABORTED_ICON_FETCH.test(messageOf(error))) return;
    delegate.handleError(error);
  }
}
